import z from "zod";

/**
 * The settings tables a request may name, and the whole list of them.
 *
 * A request asks for the organisation's defaults and, optionally, for the overrides one row of one
 * table carries. Which table that is comes from the client, so the set it can name is a closed list
 * here rather than a shape a name happens to match: the server keeps a registry saying which column
 * each of these is addressed by, and that registry is declared against this list, so a table in one
 * and not the other does not compile.
 *
 * `organizationSettings` is deliberately absent — it is the defaults every read merges onto, not a
 * scope of its own — and so is `userSettings`, which holds no setting a form writes.
 */
export const scopedSettingsTables = ["projectSettings"] as const;

export type ScopedSettingsTable = (typeof scopedSettingsTables)[number];

export const getSettingsQuerySchema = z
	.object({
		scoped: z.enum(["true", "false"]),
		table: z.enum(scopedSettingsTables).optional(),
		identifier: z.string().optional(),
	})
	.superRefine((data, context) => {
		if (data.scoped === "true" && !data.table) {
			context.addIssue({
				code: "custom",
				message: "Table is required when scoped is true",
			});
		}
		if (data.scoped === "true" && !data.identifier) {
			context.addIssue({
				code: "custom",
				message: "Identifier is required when scoped is true",
			});
		}
	});

export type GetSettingsQuerySchema = z.infer<typeof getSettingsQuerySchema>;
